import Vue from 'vue'
import Vuex from 'vuex'
import { db } from "../config/db";

Vue.use(Vuex)

const loadDocs = async ({ collectionName, predicates }) => {
  let query = db.collection(collectionName);
  if (predicates && predicates.length) {
    predicates.forEach(p => {
      query = query.where(p.key, p.op, p.val);
    });
  }
  const resultSet = await query.get();
  const docs = [];
  resultSet.forEach(doc => {
    const d = Object.assign({}, doc.data());
    d.id = doc.id;
    docs.push(d);
  });
  return docs;
};

export const store = new Vuex.Store({
  // strict: true,
  state: {
    installPrompt: null,
    error: null,
    message: '',
    unbindHandlers: {},
    groups: [],
    users: [],
    restaurants: [],
    counters: [],
    sections: [],
    assignments: [],
    services: [],
    groupProducts: [],
    restaurantOrders: [],
    restaurantServices: [],
    restaurantProducts: [],
    reports: [],
    selectedGroup: null,
    selectedRestaurant: null,
    selectedCounter: null,
    selectedSection: null,
    selectedService: null,
    selectedUser: null
  },
  mutations: {
    //--setting the lists
    SET_GROUPS: (state, groups) => {
      state.groups = groups;
    },
    SET_USERS: (state, users) => {
      state.users = users;
    },
    SET_RESTAURANTS: (state, restaurants) => {
      state.restaurants = restaurants;
    },
    SET_SECTIONS: (state, sections) => {
      state.sections = sections;
    },
    SET_ASSIGNMENTS: (state, assignments) => {
      state.assignments = assignments;
    },
    SET_COUNTERS: (state, counters) => {
      state.counters = counters;
    },
    SET_RESTAURANT_SERVICES: (state, services) => {
      state.restaurantServices = services;
    },
    SET_GROUP_PRODUCTS: (state, products) => {
      state.groupProducts = products;
    },
    SET_RESTAURANT_PRODUCTS: (state, products) => {
      state.restaurantProducts = products;
    },
    SET_RESTAURANT_ORDERS: (state, orders) => {
      state.restaurantOrders = orders;
    },
    SET_REPORTS: (state, reports) => {
      state.reports = reports;
    },
    //--selecting an item from the list
    SELECT_GROUP: (state, group) => {
      state.selectedGroup = group;
    },
    SELECT_RESTAURANT: (state, restaurant) => {
      state.selectedRestaurant = restaurant;
    },
    SELECT_SECTION: (state, section) => {
      state.selectedSection = section;
    },
    SELECT_COUNTER: (state, counter) => {
      state.selectedCounter = counter;
    },
    SELECT_SERVICE: (state, service) => {
      state.selectedService = service;
    },
    SELECT_USER: (state, user) => {
      state.selectedUser = user;
    },
  },
  actions: {
    installApp({ state }) {
      state.installPrompt.prompt();
      // Wait for the user to respond to the prompt
      state.installPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === "accepted") {
          state.installPrompt = null;
          //console.log("User accepted the A2HS prompt");
        } else {
          //console.log("User dismissed the A2HS prompt");
        }
      });
    },
    //------BINDINGS---------
    bindUsers({ state, dispatch }) {
      dispatch('bindDocs', {
        mutation: 'SET_USERS',
        collectionName: 'users',
        unbindKey: 'bindUsers',
        predicates: [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "status", op: ">=", val: 0 }]
      });
    },
    bindRestaurants({ state, dispatch }) {
      const predicates = [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "status", op: ">=", val: 0 }];
      if (state.selectedUser) {
        if (state.selectedUser && ["RM"].includes(state.selectedUser.role)) {
          predicates.push({ key: "_managerId", op: "==", val: state.selectedUser.id });
        }
      }
      dispatch('bindDocs', {
        mutation: 'SET_RESTAURANTS',
        selectMutation: 'SELECT_RESTAURANT',
        currentSelectedKey: 'selectedRestaurant',
        collectionName: 'restaurants',
        unbindKey: 'bindRestaurants',
        predicates: predicates
      });
    },
    bindSections({ state, dispatch }) {
      const predicates = [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "_restaurantKey", op: "==", val: state.selectedRestaurant.id }, { key: "status", op: ">=", val: 0 }];
      if (state.selectedUser) {
        if (["SS"].includes(state.selectedUser.role)) {
          predicates.push({ key: "_supervisorId", op: "==", val: state.selectedUser.id });
        } else if (["SW"].includes(state.selectedUser.role)) {
          predicates.push({ key: "_waiterId", op: "==", val: state.selectedUser.id });
        } else if (["SH"].includes(state.selectedUser.role)) {
          predicates.push({ key: "_helperId", op: "==", val: state.selectedUser.id });
        }
      }
      dispatch('bindDocs', {
        mutation: 'SET_SECTIONS',
        selectMutation: 'SELECT_SECTION',
        currentSelectedKey: 'selectedSection',
        collectionName: 'sections',
        unbindKey: 'bindSections',
        predicates: predicates
      });
    },
    bindCounters({ state, dispatch }) {
      const predicates = [{ key: "_groupKey", op: "==", val: state.selectedGroup.id },
      { key: "_restaurantKey", op: "==", val: state.selectedRestaurant && state.selectedRestaurant.id },
      { key: "status", op: ">=", val: 0 }];
      if (state.selectedUser) {
        if (["CC"].includes(state.selectedUser.role)) {
          predicates.push({ key: "_chefId", op: "==", val: state.selectedUser.id });
        }
      }
      dispatch('bindDocs', {
        mutation: 'SET_COUNTERS',
        selectMutation: 'SELECT_COUNTER',
        currentSelectedKey: 'selectedCounter',
        collectionName: 'counters',
        unbindKey: 'bindCounters',
        predicates: predicates
      });
    },
    bindRestaurantServices({ state, dispatch }) {
      const predicates = [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "_restaurantKey", op: "==", val: state.selectedRestaurant.id }, { key: "status", op: "<", val: 2 }
      ]; //Exclude PAID(2) orders (PAID orders will be loaded separately when needed for reporting)
      dispatch('bindDocs', {
        mutation: 'SET_RESTAURANT_SERVICES',
        selectMutation: 'SELECT_SERVICE',
        currentSelectedKey: 'selectedService',
        collectionName: 'services',
        unbindKey: 'bindRestaurantServices',
        predicates: predicates
      });
    },
    bindRestaurantProducts({ state, dispatch }) {
      dispatch('bindDocs', {
        mutation: 'SET_RESTAURANT_PRODUCTS',
        collectionName: 'products',
        unbindKey: 'bindRestaurantProducts',
        predicates: [{ key: "_groupKey", op: "==", val: state.selectedGroup.id },
        { key: "_restaurantKey", op: "==", val: state.selectedRestaurant.id },
        { key: "isGroupProduct", op: "==", val: false },
        { key: "status", op: ">=", val: 0 }]
      });
    },
    bindGroupProducts({ state, dispatch }) {
      dispatch('bindDocs', {
        mutation: 'SET_GROUP_PRODUCTS',
        collectionName: 'products',
        unbindKey: 'bindGroupProducts',
        predicates: [{ key: "_groupKey", op: "==", val: state.selectedGroup.id },
        { key: "isGroupProduct", op: "==", val: true },
        { key: "status", op: ">=", val: 0 }]
      });
    },
    bindRestaurantOrders({ state, dispatch }) {
      const predicates = [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "_restaurantKey", op: "==", val: state.selectedRestaurant.id }, { key: "status", op: "<=", val: 6 }];//Exclude PAIDINSERVICE(7) orders (PAIDINSERVICE orders will be loaded separately when needed for reporting)
      dispatch('bindDocs', {
        mutation: 'SET_RESTAURANT_ORDERS',
        collectionName: 'orders',
        unbindKey: 'bindRestaurantOrders',
        predicates: predicates
      });
    },
    bindReports({ state, dispatch }) {
      dispatch('bindDocs', {
        mutation: 'SET_REPORTS',
        collectionName: 'reports',
        unbindKey: 'bindReports',
        predicates: [{ key: "_groupKey", op: "==", val: state.selectedGroup.id }, { key: "_restaurantKey", op: "==", val: state.selectedRestaurant.id }, { key: "status", op: "==", val: 2 }] // only OPEN ones needs reactivity
      });
    },
    //generic private action for binding each of the docs
    bindDocs({ commit, state }, payLoad) {
      const {
        mutation,
        selectMutation,
        currentSelectedKey,
        collectionName,
        unbindKey,
        predicates
      } = payLoad;
      let foundCurrentSelected = false;
      if (predicates && predicates.length) {
        let query = db.collection(collectionName);
        predicates.forEach(p => {
          query = query.where(p.key, p.op, p.val);
        });
        unbindKey && state.unbindHandlers[unbindKey] && state.unbindHandlers[unbindKey]();
        state.unbindHandlers[unbindKey] = query.onSnapshot(snapshot => {
          const docs = [];
          snapshot.forEach(dbDoc => {
            const doc = { id: dbDoc.id, ...dbDoc.data() };
            if (currentSelectedKey && state[currentSelectedKey] && state[currentSelectedKey].id === doc.id) {
              foundCurrentSelected = true;
              if (selectMutation) {
                commit(selectMutation, doc);
              }
            }
            docs.push(doc);
          });
          docs.sort((d1, d2) => {
            let retVal = 0;
            if (d1.code) {
              if (d1.code > d2.code) retVal = 1;
              if (d1.code < d2.code) retVal = -1;
            } else {
              const ts1 = Number(d1.lastUpdatedTS || d1.createdTS);
              const ts2 = Number(d2.lastUpdatedTS || d2.createdTS);
              if (ts1 < ts2) retVal = 1;
              if (ts1 > ts2) retVal = -1;
            }
            return retVal;
          });
          commit(mutation, docs);
          //let the default selected Item be the first one when the collection reloads
          if (!foundCurrentSelected && selectMutation) {
            commit(selectMutation, docs.length ? docs[0] : null);
          }
        });
      }
    },
    createDoc({ state }, payLoad) {
      const { collectionName, doc } = payLoad;
      doc.createdTS = Date.now();
      doc.createdBy = state.selectedUser ? state.selectedUser.id : "Unknown";
      doc.status = doc.status || 0;
      db.collection(collectionName)
        .add(doc)
        .then(response => {
          state.message = response ? "Created successfully." : "";
        })
        .catch(error => {
          state.error = "Error while creating." + error;
        });
    },
    async createDocSync({ state }, payLoad) {
      const { collectionName, doc } = payLoad;
      const response = await db.collection(collectionName).add(doc);
      state.message = response ? "Created successfully." : "";
      return response;
    },
    async readDocByIdSync({ state }, payLoad) {
      const { collectionName, docId } = payLoad;
      let query = db.collection(collectionName);
      const docRef = await query.doc(docId).get();
      state.message = docRef.exists ? "Retrieved doc." : "Doc empty";
      const doc = docRef.exists ? { id: docRef.id, ...docRef.data() } : false;
      return doc;
    },
    async updateDocSync({ state }, payLoad) {
      const { collectionName, docId, doc } = payLoad;
      doc.lastUpdatedTS = Date.now();
      doc.lastUpdatedBy = state.selectedUser ? state.selectedUser.id : "Unknown";
      const response = await db.collection(collectionName).doc(docId).set(doc);
      state.message = response ? "Updated successfully." : "";
      return response;
    },
    updateDoc({ state }, payLoad) {
      const { collectionName, docId, doc } = payLoad;
      doc.lastUpdatedTS = Date.now();
      doc.lastUpdatedBy = state.selectedUser ? state.selectedUser.id : "Unknown";
      db.collection(collectionName)
        .doc(docId)
        .set(doc)
        .then(response => {
          this.message = response ? "Updated doc successfully." : "";
        })
        .catch(error => {
          this.error = "Error while updating doc." + error;
        });
    },
    updateFields({ state }, payLoad) {
      const { collectionName, docId, subDoc } = payLoad;
      subDoc.lastUpdatedTS = Date.now();
      subDoc.lastUpdatedBy = state.selectedUser ? state.selectedUser.id : "Unknown";
      db.collection(collectionName)
        .doc(docId)
        .update(subDoc)
        .then(response => {
          this.message = response ? "Updated doc fields successfully." : "";
        })
        .catch(error => {
          this.error = "Error while updating doc fields." + error;
        });
    },
    deleteDoc(context, payLoad) {
      const { collectionName, docId } = payLoad;
      db.collection(collectionName)
        .doc(docId)
        .delete()
        .then(response => {
          this.message = response ? "Deleted successfully." : "";
        })
        .catch(error => {
          this.error = "Error while deleting. " + error;
        });
    },
    async loadDocs(context, payLoad) {
      return await loadDocs(payLoad);
    },
    //-------SELECTS-----------
    selectGroup({ commit, state }, group) {
      if (group && group !== state.selectedGroup) {
        commit('SELECT_GROUP', group);
        commit('SELECT_RESTAURANT', null);
        commit('SELECT_SECTION', null);
        commit('SELECT_COUNTER', null);
      }
    },
    selectRestaurant({ commit, state }, restaurant) {
      if (!restaurant) restaurant = state.restaurants[0];//default
      if (restaurant && restaurant !== state.selectedRestaurant) {
        commit('SELECT_RESTAURANT', restaurant);
      }
    },
    selectSection({ commit, state }, section) {
      if (!section) section = state.sections[0];//default
      if (section !== state.selectedSection) {
        commit('SELECT_SECTION', section);
      }
    },
    selectCounter({ commit, state }, counter) {
      if (!counter) counter = state.counters[0];//default
      if (counter !== state.selectedCounter) {
        commit('SELECT_COUNTER', counter);
      }
    },
    selectService({ commit, state }, service) {
      if (!service) service = state.services[0];//default
      if (service !== state.selectedService) {
        commit('SELECT_SERVICE', service);
      }
    },
    selectUser({ commit, state }, user) {
      if (user !== state.selectedUser) {
        commit('SELECT_USER', user);
      }
    },
    unbindAll({ state }) {
      const keys = Object.keys(state.unbindHandlers);
      keys.forEach(key => state.unbindHandlers[key]());
    }
  },
  getters: {
    draftRestaurantServices: state => {
      return state.restaurantServices.filter(s => s.status === 0);
    },
    activeRestaurantServices: state => {
      return state.restaurantServices.filter(s => s.status === 1);
    },
    paidRestaurantServices: state => {
      return state.restaurantServices.filter(s => s.status === 2);
    },
    closedRestaurantServices: state => {
      return state.restaurantServices.filter(s => s.status === 3);
    },
    sectionServices: state => {
      return state.restaurantServices.filter(s => s._sectionKey === state.selectedSection.id);
    },
    draftSectionServices: state => {
      return state.restaurantServices.filter(s => s._sectionKey === (state.selectedSection ? state.selectedSection.id : "") && s.status === 0);
    },
    activeSectionServices: state => {
      return state.restaurantServices.filter(s => s._sectionKey === (state.selectedSection ? state.selectedSection.id : "") && s.status === 1);
    },
    paidSectionServices: state => {
      return state.restaurantServices.filter(s => s._sectionKey === (state.selectedSection ? state.selectedSection.id : "") && s.status === 2);
    },
    closedSectionServices: state => {
      return state.restaurantServices.filter(s => s._sectionKey === (state.selectedSection ? state.selectedSection.id : "") && s.status === 3);
    },
    serviceOrders: state => {
      return state.restaurantOrders.filter(s => s._serviceKey === (state.selectedService ? state.selectedService.id : ""));
    },
    sectionOrders: state => {
      return state.restaurantOrders.filter(s => s._sectionKey === (state.selectedSection ? state.selectedSection.id : ""));
    },
    counterOrders: state => {
      return state.restaurantOrders.filter(s => s._counterKey === (state.selectedCounter ? state.selectedCounter.id : ""));
    },
    restaurantReport: state => {
      return state.reports.find(r => r._restaurantKey === (state.selectedRestaurant ? state.selectedRestaurant.id : "") && r.reportType === "restaurant" && r.status === 2);
    },
    sectionReport: state => (sectionId) => {
      return state.reports.find(r => r._sectionKey === (sectionId || (state.selectedSection && state.selectedSection.id)) && r.reportType === "section" && r.status === 2);
    },
    counterReport: state => (counterId) => {
      return state.reports.find(r => r._counterKey === (counterId || (state.selectedCounter && state.selectedCounter.id)) && r.reportType === "counter" && r.status === 2);
    },
    userAssignments: state => {
      return state.assignments.find(r => r._userKey === (state.selectedUser ? state.selectedUser.id : ""));
    },
    allowedRestaurants: async (state) => {
      const user = state.selectedUser;
      let myRestaurants = state.restaurants.filter(r => r.status > 0);//[];
      if (user) {
        if (["GA"].includes(user.role)) {
          myRestaurants = state.restaurants;
        } else if (["RM"].includes(user.role)) {
          myRestaurants = state.restaurants.filter(r =>
            [r._managerId].includes(user.id)
          );
        } else if (["SS", "SW", "SH"].includes(user.role)) {
          const s = await loadDocs({ collectionName: "sections" })
          const mySectionsParentIds = s.filter(a =>
            [a._supervisorId, a._waiterId, a._helperId].includes(user.id)
          ).map(s => s._restaurantKey);
          myRestaurants = state.restaurants.filter(r =>
            mySectionsParentIds.includes(r.id)
          );
        } else if (["CC"].includes(user.role)) {
          const s = await loadDocs({ collectionName: "counters" })
          const myCountersParentIds = s.filter(a =>
            [a._chefId].includes(user.id)
          ).map(s => s._restaurantKey);
          myRestaurants = state.restaurants.filter(r =>
            myCountersParentIds.includes(r.id)
          );
        }
      }
      if (myRestaurants.length && state.selectedRestaurant) {
        const found = myRestaurants.find(r => r.id === (state.selectedRestaurant && state.selectedRestaurant.id));
        state.selectedRestaurant = found || myRestaurants[0];
      }
      return myRestaurants;
    },
    mySections: state => {
      const user = state.selectedUser;
      let mySections = [];
      if (user) {
        if (["GA", "RM"].includes(user.role)) {
          mySections = state.sections.filter(s => s.status > 0);
        } else if (["SS", "SW", "SH"].includes(user.role)) {
          mySections = state.sections.filter(s =>
            [s._supervisorId, s._waiterId, s._helperId].includes(user.id) && s.status > 0
          );
        }
      }
      return mySections;
    },
    myServices: state => {
      const user = state.selectedUser;
      let myServices = [];
      if (user) {
        if (["GA", "RM"].includes(user.role)) {
          myServices = state.restaurantServices;
        } else if (["SS", "SW", "SH"].includes(user.role)) {
          const mySectionIds = state.sections.filter(s =>
            [s._supervisorId, s._waiterId, s._helperId].includes(user.id)
          ).map(s => s.id);
          myServices = state.restaurantServices.filter(s => mySectionIds.includes(s._sectionKey));
        }
      }
      return myServices;
    },
    myCounters: state => {
      const user = state.selectedUser;
      let myCounters = [];
      if (user) {
        if (["GA", "RM"].includes(user.role)) {
          myCounters = state.counters.filter(s => s.status > 0);
        } else if (["CC"].includes(user.role)) {
          myCounters = state.counters.filter(s =>
            [s._chefId].includes(user.id) && s.status > 0
          );
        }
      }
      return myCounters;
    },
    myProducts: (state, getters) => {
      const user = state.selectedUser;
      let myProducts = [];
      if (user) {
        if (getters.myFeatures.eligibleFeatures.includes('INVENTORY')) {
          if (["GA", "RM"].includes(user.role)) {
            myProducts = state.restaurantProducts;
          } else if (["CC"].includes(user.role)) {
            const myCounterIds = state.counters.filter(c =>
              [c._chefId].includes(user.id)
            ).map(c => c.id);
            myProducts = state.restaurantProducts.filter(p => myCounterIds.includes(p._counterKey));
          }
        } else {
          myProducts = state.groupProducts;
        }
      }
      return myProducts;
    },
    myOrders: state => {
      const user = state.selectedUser;
      let myOrders = [];
      if (user) {
        if (["GA", "RM"].includes(user.role)) {
          myOrders = state.restaurantOrders;
        } else if (["SS", "SW", "SH"].includes(user.role)) {
          const mySectionIds = state.sections.filter(s =>
            [s._supervisorId, s._waiterId, s._helperId].includes(user.id)
          ).map(s => s.id);
          myOrders = state.restaurantOrders.filter(o => mySectionIds.includes(o._sectionKey));
        } else if (["CC"].includes(user.role)) {
          const myCounterIds = state.counters.filter(c =>
            [c._chefId].includes(user.id)
          ).map(c => c.id);
          myOrders = state.restaurantOrders.filter(o => myCounterIds.includes(o._counterKey));
        }
      }
      return myOrders;
    },
    myFeatures: state => {
      const paidFeatures = ['REPORT', 'TIMER', 'BILLSPLIT', 'INCENTIVE', 'WORKFLOW', 'VOICE', 'INVENTORY', 'TAX'];
      let myFeatures = {
        eligibleCounts: { R: 1, S: 1, C: 1, P: 99, U: 1, O: 100000, B: 7 },
        eligibleFeatures: []
      }
      const pricingPlan = state.selectedGroup ? state.selectedGroup.pricingPlan : null;
      if (state.selectedGroup && state.selectedGroup.features) {
        myFeatures = JSON.parse(state.selectedGroup.features);
      } else {
        switch (pricingPlan) {
          case 'Nucleus':
            myFeatures.eligibleFeatures = [];
            break;
          case 'Atom':
            myFeatures.eligibleCounts.R = 1;
            myFeatures.eligibleCounts.C = 10;
            myFeatures.eligibleCounts.S = 10;
            myFeatures.eligibleCounts.P = 999;
            myFeatures.eligibleCounts.U = 50;
            myFeatures.eligibleCounts.O = 10000; //per day
            myFeatures.eligibleCounts.B = 30; // 30 days(1 week) Backup
            myFeatures.eligibleFeatures = paidFeatures;
            break;
          case 'Molecule':
            myFeatures.eligibleCounts.R = 10;
            myFeatures.eligibleCounts.C = 10;
            myFeatures.eligibleCounts.S = 10;
            myFeatures.eligibleCounts.P = 999;
            myFeatures.eligibleCounts.U = 500;
            myFeatures.eligibleCounts.O = 10000; //per day
            myFeatures.eligibleCounts.B = 90; // 90 days(1 week) Backup
            myFeatures.eligibleFeatures = paidFeatures;
            break;
          case 'Organic':
            myFeatures.eligibleCounts.R = 25;
            myFeatures.eligibleCounts.C = 10;
            myFeatures.eligibleCounts.S = 10;
            myFeatures.eligibleCounts.P = 999;
            myFeatures.eligibleCounts.U = 1500;
            myFeatures.eligibleCounts.O = 10000; //per day
            myFeatures.eligibleCounts.B = 180; // 180 days(1 week) Backup
            myFeatures.eligibleFeatures = paidFeatures;
            break;
          default:
            break;
        }
      }
      return myFeatures;
    }
  }
})